import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../services/projectService';
import ProjectSharingModal from './ProjectSharingModal';
import { getRoomsForMultipleStructures } from '../services/structureService';
import { authService } from '../services/authService';
import { useLoading } from '../contexts/LoadingContext';
import useProjectDesign from '../hooks/useProjectDesign';
import ProjectPlansTab from './project/ProjectPlansTab';
import ProjectNotesTab from './project/ProjectNotesTab';
import LogoPopup from './LogoPopup';
import DocumentsHub from './documents/DocumentsHub';
import NotificationBubble from './NotificationBubble';
import ConfirmDialog from './ConfirmDialog';
import AdminBadge from './AdminBadge';
import ConstructionDocCreationModal from './ConstructionDocCreationModal';
import ProjectPhotoGallery from './ProjectPhotoGallery';
import { useAdmin } from '../hooks/useAdmin';
import {
  Button,
  Heading,
  PageContainer,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from './ui';
import '../styles/ProjectDetail.css';
import { generateConstructionDocument } from '../utils/pdfGenerator';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    project,
    structures,
    roomCounts,
    constructionDocs,
    setConstructionDocs,
    userAccessLevel,
    canEditProject,
    mayCreateProjects,
    loading,
    error,
    reload,
  } = useProjectDesign(id, { overlay: true });
  const [activeTab, setActiveTab] = useState('plans'); // 'plans', 'photos', 'documents', 'notes'
  const [notification, setNotification] = useState(null);
  const [showConstructionDocModal, setShowConstructionDocModal] =
    useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCreateBlankConstructionDoc = async () => {
    try {
      showLoading();
      setShowConstructionDocModal(false);

      // Prepare project data for PDF generation
      const projectData = {
        name: project.name,
        address: project.address || '',
        description: project.description || '',
        date: new Date(),
        projectNumber: id.substring(0, 8).toUpperCase(),
      };

      // Generate the PDF base template
      const pdf = generateConstructionDocument(projectData);

      // Convert PDF to blob for upload
      const pdfBlob = pdf.output('blob');

      // Create filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${project.name.replace(/[^a-z0-9]/gi, '_')}_CDs_${timestamp}.pdf`;

      // Upload to Firebase Storage
      const storagePath = `projects/${id}/construction-docs/${filename}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, pdfBlob);
      const downloadUrl = await getDownloadURL(storageRef);

      // Prepare document metadata
      const docMetadata = {
        filename,
        baseUrl: downloadUrl,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        type: 'construction-document',
        storagePath,
        placedElements: [],
        selectedStructures: [],
        isAutoGenerated: false,
      };

      // Update project with new construction document
      const updatedDocs = [...(constructionDocs || []), docMetadata];
      await projectService.updateProject(id, {
        constructionDocs: updatedDocs,
      });

      // Update local state
      setConstructionDocs(updatedDocs);

      // Navigate to the editor for the new document
      const newDocIndex = updatedDocs.length - 1;
      navigate(`/project/${id}/cd-editor/${newDocIndex}`);

      // Show success notification
      setNotification({
        message: 'Construction document created! Opening editor...',
        type: 'success',
      });
    } catch (error) {
      console.error('Error creating construction documents:', error);
      setNotification({
        message: 'Failed to create construction documents. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  const handleGenerateFromScans = async (
    selectedStructureIds,
    progressCallback
  ) => {
    try {
      setShowConstructionDocModal(false);
      showLoading();

      // Validate structures
      const selectedStructureObjects = structures.filter((s) =>
        selectedStructureIds.includes(s.id)
      );

      if (selectedStructureObjects.length === 0) {
        throw new Error('No valid structures selected');
      }

      // Load rooms for all selected structures
      progressCallback(10);
      const allRooms =
        await getRoomsForMultipleStructures(selectedStructureIds);

      // Check for structures with no rooms
      const structuresWithNoRooms = selectedStructureObjects.filter(
        (struct) => {
          const structureRooms = allRooms.filter(
            (r) => r.structureId === struct.id
          );
          return structureRooms.length === 0;
        }
      );

      if (structuresWithNoRooms.length > 0) {
        const failedNames = structuresWithNoRooms
          .map((s) => s.name || 'Untitled')
          .join(', ');
        throw new Error(
          `The following structures have no rooms and cannot be included: ${failedNames}`
        );
      }

      progressCallback(30);

      // Create document metadata marking it as auto-generated
      const structureNames = selectedStructureObjects
        .map((s) => s.name || 'Untitled')
        .join(', ');
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Construction_Docs_${timestamp}.pdf`;

      const docMetadata = {
        filename,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        type: 'construction-document',
        selectedStructures: selectedStructureIds,
        structureNames,
        isAutoGenerated: true,
        placedElements: [],
      };

      progressCallback(50);

      // Update project with new construction document
      const updatedDocs = [...(constructionDocs || []), docMetadata];
      await projectService.updateProject(id, {
        constructionDocs: updatedDocs,
      });

      // Update local state
      setConstructionDocs(updatedDocs);

      progressCallback(100);

      // Navigate to the editor for the new document
      const newDocIndex = updatedDocs.length - 1;
      navigate(`/project/${id}/cd-editor/${newDocIndex}`);

      // Show success notification
      setNotification({
        message: 'Construction document created! Opening editor...',
        type: 'success',
      });
    } catch (error) {
      console.error('Error generating construction documents:', error);
      setNotification({
        message: error.message || 'Failed to generate construction documents.',
        type: 'error',
      });
      throw error;
    } finally {
      hideLoading();
    }
  };

  // Handle copying shared project
  const handleCopyProject = async () => {
    try {
      showLoading();
      const result = await projectService.copySharedProject(id);
      setNotification({
        message: 'Project copied successfully! Redirecting...',
        type: 'success',
      });
      setTimeout(() => {
        navigate(`/project/${result.newProjectId}`);
      }, 1500);
    } catch (error) {
      console.error('Failed to copy project:', error);
      setNotification({
        message: 'Failed to copy project. Please try again.',
        type: 'error',
      });
    } finally {
      hideLoading();
    }
  };

  // only the first read blanks the page: a reload (after a rename, a
  // reprocess) keeps it up under the overlay, so the tabs keep their state
  if (loading) {
    return null; // LoadingProvider will show the loading screen
  }

  if (error || !project) {
    return (
      <div className="project-detail-container">
        <div className="error-message">
          {error || 'Project not found'}
          <Button
            variant="secondary"
            tone="dark"
            onClick={() => navigate('/')}
            className="back-button"
          >
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-container">
      <header className="project-detail-header">
        <div className="header-content">
          <div className="header-left">
            <LogoPopup onLogout={handleLogout} />
            <Heading level={1} tone="dark" className="project-detail-title">
              {project.name}
            </Heading>
            <AdminBadge className="project-detail-admin-badge" />
          </div>
          <div className="header-right">
            {userAccessLevel === 'owner' && (
              <Button
                variant="secondary"
                tone="dark"
                onClick={() => setShowSharingModal(true)}
              >
                Share Project
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Admin warning banner. Admins can READ any project but not edit it
          (useProjectAccess: only the owner and their organization's editors
          may write), so this is where they get the supported way to change
          one — take a copy. */}
      {isAdmin &&
        !adminLoading &&
        userAccessLevel !== null &&
        !canEditProject && (
          <div className="admin-warning-banner">
            <AdminBadge className="admin-warning-banner__badge" />
            <span>You do not own this project — copy it to make changes.</span>
            <Button
              variant="soft"
              size="sm"
              className="copy-project-button"
              onClick={() => setShowCopyConfirm(true)}
            >
              Make a Copy
            </Button>
          </div>
        )}

      {/* View-only banner for shared projects */}
      {userAccessLevel === 'viewer' && (
        <div className="view-only-banner">
          <span className="view-only-label">View-Only Access</span>
          <span className="view-only-message">
            You can browse this project but cannot make changes
          </span>
          {mayCreateProjects && (
            <Button
              variant="soft"
              size="sm"
              className="copy-project-button"
              onClick={() => setShowCopyConfirm(true)}
            >
              Make a Copy
            </Button>
          )}
        </div>
      )}

      <main className="project-detail-main">
        <PageContainer maxWidth="app">
          <div className="project-overview">
            <div className="project-image-container">
              {project.imageUrl ? (
                <img
                  src={project.imageUrl}
                  alt={project.name}
                  className="project-image"
                />
              ) : (
                <div className="project-image-placeholder">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 22V12H15V22"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
            <div className="project-details">
              <p>{project.description || 'No description available'}</p>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            variant="folder"
            tone="dark"
          >
            <TabList>
              <Tab value="plans">Plans</Tab>
              <Tab value="photos">Photos</Tab>
              <Tab value="documents">Documents</Tab>
              <Tab value="notes">Notes</Tab>
            </TabList>

            {/* === PLANS TAB === */}
            <TabPanel value="plans" keepMounted>
              <ProjectPlansTab
                projectId={id}
                project={project}
                structures={structures}
                roomCounts={roomCounts}
                canEditProject={canEditProject}
                onReload={reload}
                notify={setNotification}
              />
            </TabPanel>

            {/* === PHOTOS TAB === */}
            <TabPanel value="photos" keepMounted>
              <section className="content-section">
                <ProjectPhotoGallery
                  project={project}
                  projectId={id}
                  canEdit={canEditProject}
                  onProjectUpdate={reload}
                />
              </section>
            </TabPanel>

            {/* === DOCUMENTS TAB === */}
            <TabPanel value="documents" keepMounted>
              <DocumentsHub embedded projectId={id} />
            </TabPanel>

            {/* === NOTES TAB === */}
            <TabPanel value="notes" keepMounted>
              <ProjectNotesTab
                projectId={id}
                initialNotes={project.notes || ''}
                canEdit={canEditProject}
              />
            </TabPanel>
          </Tabs>
        </PageContainer>
      </main>

      {notification && (
        <NotificationBubble
          {...notification}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Project Sharing Modal */}
      <ProjectSharingModal
        project={project}
        isOpen={showSharingModal}
        onClose={() => setShowSharingModal(false)}
      />

      {/* Copy Project Confirmation Dialog */}
      {showCopyConfirm && (
        <ConfirmDialog
          isOpen={showCopyConfirm}
          onConfirm={() => {
            setShowCopyConfirm(false);
            handleCopyProject();
          }}
          onCancel={() => setShowCopyConfirm(false)}
          title="Copy This Project?"
          message="This will create your own editable copy of this project and all its structures."
          confirmText="Copy Project"
          cancelText="Cancel"
        />
      )}

      {/* Construction Doc Creation Modal */}
      <ConstructionDocCreationModal
        isOpen={showConstructionDocModal}
        onClose={() => setShowConstructionDocModal(false)}
        onCreateBlank={handleCreateBlankConstructionDoc}
        onGenerateFromScans={handleGenerateFromScans}
        structures={structures}
      />
    </div>
  );
}

export default ProjectDetail;
